import { MediaMatcher } from '@angular/cdk/layout';
import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { BREAKPOINTS, Layout } from './breakpoints';

@Injectable({
  providedIn: 'root',
})
export class BreakpointService {
  private readonly matcher = inject(MediaMatcher);

  private readonly minMd = this.watch(`(min-width: ${BREAKPOINTS.md}px)`);
  private readonly minLg = this.watch(`(min-width: ${BREAKPOINTS.lg}px)`);
  private readonly minXl = this.watch(`(min-width: ${BREAKPOINTS.xl}px)`);

  readonly layout: Signal<Layout> = computed(() => {
    if (this.minXl()) return 'desktop';
    if (this.minLg()) return 'tablet-landscape';
    if (this.minMd()) return 'tablet';
    return 'phone';
  });
  readonly layout$: Observable<Layout> = toObservable(this.layout);

  readonly isPhone = computed(() => this.layout() === 'phone');
  readonly isTablet = computed(() => this.layout() === 'tablet');
  readonly isTabletLandscape = computed(() => this.layout() === 'tablet-landscape');
  readonly isDesktop = computed(() => this.layout() === 'desktop');
  /** Below 1024px: overlay drawers, list/map toggles. */
  readonly isCompact = computed(() => !this.minLg());

  readonly isCoarsePointer: Signal<boolean> = this.watch('(pointer: coarse)');
  readonly prefersReducedMotion: Signal<boolean> = this.watch('(prefers-reduced-motion: reduce)');

  private watch(query: string): Signal<boolean> {
    const mql = this.matcher.matchMedia(query);
    const state = signal(mql.matches);
    mql.addEventListener('change', (event) => state.set(event.matches));
    return state.asReadonly();
  }
}
